import type { ReactNode } from "react";
import { ScrollView, StyleSheet } from "react-native";

import { Dialog } from "../primitives/Dialog";
import { PageSheet } from "../primitives/PageSheet";
import { useBreakpoint } from "../useBreakpoint";
import type { UiTheme } from "../theme";

// The "Avvia & Sfoglia" hub — the surface the tab-bar `+` (mobile) and the
// sidebar "Crea" CTA (web) both open. ONE responsive tree (redesign track HR),
// composed from the CN-3 modal primitives (no hand-rolled overlay chrome):
//   narrow → full-screen <PageSheet> (the phone experience)
//   wide   → a large centred <Dialog> (maxWidth 720 + a ✕ close header)
// No Platform.OS branch — the split is width-driven via useBreakpoint (768px).
// This is the SHELL only; its body (the 4 actions, then the library browser)
// is passed as children so each slice fills it without touching the frame.
//
// Safe-area insets are injected as props (topInset/bottomInset) — @heraion/ui
// carries no safe-area-context dep, so the mobile mount feeds useSafeAreaInsets;
// web passes none. (The wide <Dialog> is a floating card — no insets needed.)

export type CreateHubProps = {
  theme: UiTheme;
  visible: boolean;
  onClose: () => void;
  title: string;
  closeLabel: string;
  topInset?: number;
  bottomInset?: number;
  children: ReactNode;
};

export function CreateHub({
  theme,
  visible,
  onClose,
  title,
  closeLabel,
  topInset = 0,
  bottomInset = 0,
  children,
}: CreateHubProps) {
  const { wide } = useBreakpoint();

  // Narrow: the full-screen page-sheet primitive verbatim.
  if (!wide) {
    return (
      <PageSheet
        theme={theme}
        visible={visible}
        onRequestClose={onClose}
        title={title}
        cancelLabel={closeLabel}
        topInset={topInset}
        bottomInset={bottomInset}
      >
        <ScrollView contentContainerStyle={styles(theme).narrowBody}>
          {children}
        </ScrollView>
      </PageSheet>
    );
  }

  // Wide: the shared centred-card dialog, widened, with a ✕ close header.
  // Dialog caps the card at 85% viewport but owns no scroll (MergeTargetPicker
  // shows the same primitive expects the caller to wrap a long body itself).
  return (
    <Dialog
      theme={theme}
      visible={visible}
      onRequestClose={onClose}
      title={title}
      closeLabel={closeLabel}
      maxWidth={720}
    >
      <ScrollView style={styles(theme).wideBody}>{children}</ScrollView>
    </Dialog>
  );
}

function styles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    narrowBody: { padding: s.s5, gap: s.s4 },
    wideBody: { flexGrow: 1, flexShrink: 1 },
  });
}
